import type { DateTime } from 'luxon';
import type { TableReportGroupType, TopicChartType } from '../mapper/groupTopicReportMapper';
import type { NewStudentTutorDataInterface, TimeAssessmentFilterEnum } from '@whizz/api-model';
import { TickStatusEnum } from '@whizz/api-model';

export type StudentDataType = 'progression' | 'qualityLearningTime' | 'mathsAgeImprovement' | 'mathsAge';
export type ProgressDataType = 'mathsAgeDelta' | 'initialAssessment' | StudentDataType;
export type StudentTypeWithReassessment = StudentType & Pick<NewStudentTutorDataInterface, 'hasBeenReassessed' | 'reassessmentEndDate'>;

export const student_assessment_state = [ 'not_logged_in', 'not_started', 'started', 'complete' ] as const;
export type StudentAssessmentStateType = typeof student_assessment_state[number];

export const student_state = [ 'started', 'no_logged', 'no_assessment', 'no_progression', 'in_reassessment' ] as const;
export type StudentStateType = typeof student_state[number];

export const your_students_state = [ 'failed_2', 'good_usage_low_progressions', 'struggling', 'reassessed', 'slow_progress' ];
export type YourStudentsStateType = typeof your_students_state[number];

export const your_students_recent_state = [ 'great_job', 'no_logging' ];
export type YourStudentsRecentStateType = typeof your_students_recent_state[number];

export type StudentLoginProps = {
  username: string;
};

export type StudentLessonsProps = {
  [key: string]: { // YourStudentsStateType
    topic: string;
    lesson: string;
    lessonId: string;
  }
};

export type StudentType = {
  /**
   * Student id.
   */
  id: number;
  /**
   * Student first name.
   */
  firstName: string;
  /**
   * Student full name.
   */
  fullName: string;
  /**
   * Student maths age.
   */
  maths_age?: number;
  /**
   * Student gender.
   */
  gender?: number | null;
  /**
   * Student relevant date (`assessment started` or `logging date` in case the first is null)
   */
  haveNotCompletedRelevantDate?: DateTime
  /**
   * Student progress so far.
   */
  progress?: number;
  /**
   * Student login details.
   */
  loginDetails?: StudentLoginProps;
  /**
   * Student lesson details.
   */
  lessonsDetails?: StudentLessonsProps;
  /**
   * Student state.
   */
  state?: StudentStateType;
  /**
   * Your students block state.
   */
  yourStudentsStates?: YourStudentsStateType[];
  /**
   * Student charts data.
   */
  chartsData?: Record<StudentDataType, number>;
  lastAssessmentDate?: DateTime | string | null;
  color?: string | null; //Added because TS was moaning in TopicProgress.tsx. Todo: Fix this
  lastLogonDate?: NewStudentTutorDataInterface['lastLogonDate'];
};

export type ClassType = {
  id: number;
  fullName: string;
  color?: string | null;
  chartsData?: Record<StudentDataType, number>;
};

export type YearType = ClassType;

export type AverageReportType = {
  // Main metrics
  mathsAgeAverage: number;
  mathsAgeImprovementAverage: number;
  progressionAverage: number;
  qualityLearningTimeAverage: number;
  consistencyAverage: number;
  consistencyAverageName: string;
  tickStatusAverage: string;
  studentStatusAverage: string;

  // Additional metrics
  timeSpanAverage: number
  mathsAgeAtStartAverage: number
  tutorUsageAverage: number
  topicChallengeUsageAverage: number
  progressionPassRateAverage: number
  exerciseMarkAverage: number
  testMarkAverage: number
  actualAgeAverage: number
  minutesPerProgressionAverage: number
};

export type ClassReportType = {
  students: {
    all: ClassReportStudentType[];
    unassessedOrReassessedStudents: ClassReportStudentType[];
    assessedNotBeingReassessedStudents: ClassReportStudentType[];
  };
} & AverageReportType;

export type ClassReportStudentType = {
  // Student basic props
  id: number;
  childId: number;
  fullName: string;
  firstName: string;
  gender: number | null;
  selected: boolean;

  // Main metrics
  consistency: number | null;
  consistencyName: string | null;
  tickStatus: string;
  studentStatus: string;
  progressions: number;
  chartsData: Record<StudentDataType, number>;

  // Additional metrics
  lastAssessmentDate: DateTime | string | null;
  timeSpan: number;
  mathsAgeAtStart: number;
  tutorUsage: number;
  topicChallengeUsage: number;
  topicChallengesPassed: {
    count: number,
    total: number,
  };
  progressionPassRate: number;
  exerciseMark: number;
  testMark: number;
  actualAge: number;
  minutesPerProgression: number;
  parentLink: boolean;
  tags: number[];
};

export type HighlightedProps = {
  /**
   * Label for highlighted element(s).
   */
  highlightedLabel: string | null;
  /**
   * Highlighted element(s) id.
   */
  highlightedIds: number[];
  onElementClick?: (studentId: number) => void;
};

export type GroupType = ClassType & {
  totalStudentsNum: number
  assessedStudents: number
  unassessedStudents: number
  chartsData?: Record<ProgressDataType, number>;
};

export type SchoolReportType = {
  classes: (GroupType & AverageReportType)[]
  yearGroups: (GroupType & AverageReportType)[]
  average: AverageReportType
  yearGroupsAverage: AverageReportType
};

export type PdfFilterType = {
  withMathsAge: boolean
  withAverages: boolean
  withCharts: boolean
  classSelectedId: number
  dateIntervalSelected: DateRangeOptionsType
  startDate: DateTime | TimeAssessmentFilterEnum
  endDate: DateTime | null
};

export type SchoolTopicChartType = { classes: TopicChartType[], yearGroups: TopicChartType[] };
export type SchoolTopicTableType = { classes: TableReportGroupType, yearGroups: TableReportGroupType };

export type ReportsSectionsType = 'maths_age' | 'maths_age_improvements' | 'progressions' | 'quality' | 'consistency' | 'student_status' | 'topic_progress';
export type SchoolReportsSectionsType = 'maths_age_delta' | 'maths_age_improvements' | 'progressions' | 'quality' | 'consistency' | 'initial_assessment' | 'topic_progress';

export type ReportsViewType = 'charts' | 'table';
export type SchoolViewType = 'class' | 'year';

export type DateRangeOptionsType = 'last_x' | 'last_week' | 'since_x' | 'current_acYear' | 'since_last' | 'full' | 'custom';
export const date_range: DateRangeOptionsType[] = [ 'last_x', 'last_week', 'since_x', 'current_acYear', 'since_last', 'full', 'custom' ];

export type ReportsFilterOptionsType = 'student' | 'quality' | 'progression' | 'gender' | 'tag';
export const reports_filters: ReportsFilterOptionsType[] = [ 'student', 'gender', 'quality', 'progression', 'tag' ];

export type RecentDateOptionsType = 'last_week' | 'since_x' | 'today';
export const recent_date: RecentDateOptionsType[] = [ 'last_week', 'since_x', 'today' ];

export type SinceDateOptionsType = 'since_day_1' | 'since_day_2' | 'since_day_3' | 'since_day_4' | 'since_day_5' | 'since_day_6' | 'since_day_7';
export const since_date: SinceDateOptionsType[] = [ 'since_day_1', 'since_day_2', 'since_day_3', 'since_day_4', 'since_day_5', 'since_day_6', 'since_day_7' ];

export type DownloadSettingsType = 'maths_age' | 'class_average' | 'class_chart';
export const download_settings: DownloadSettingsType[] =  [ 'maths_age', 'class_average', 'class_chart' ];

export type MetricsSettingsType = 'students' | 'unassessed' | 'maths_age' | 'maths_age_delta' | 'maths_age_improvements' | 'progressions' | 'quality' | 'consistency' | 'green_tick' | 'student_status' |
'last_assessment' | 'time_span' | 'maths_age_at_start' | 'tutor_usage' | 'topic_challenge_usage' | 'topic_challenges_passed' | 'progression_pass_rate' | 'exercise_mark_avg' | 'test_mark_avg' | 'age' | 'minutes' | 'parent';
export const default_metrics_settings: MetricsSettingsType[] =  [ 'maths_age', 'maths_age_improvements', 'progressions', 'quality', 'green_tick', 'student_status' ];
export const additional_metrics_settings: MetricsSettingsType[] =
    [ 'last_assessment', 'age', 'maths_age_at_start', 'time_span', 'tutor_usage', 'topic_challenge_usage', 'topic_challenges_passed', 'minutes', 'progression_pass_rate', 'exercise_mark_avg', 'test_mark_avg', 'parent' ];
export const school_default_metrics_setting: MetricsSettingsType[] = [ 'students', 'unassessed', 'maths_age_delta',  ...(default_metrics_settings.filter((metric) => ![ 'maths_age', 'student_status' ].includes(metric))) ];
export const school_additional_metrics_settings: MetricsSettingsType[] =
    [ 'age', 'maths_age_at_start', 'time_span', 'tutor_usage', 'topic_challenge_usage', 'minutes', 'progression_pass_rate', 'exercise_mark_avg', 'test_mark_avg' ];

export type ReportsProgressionFilterType = 'below' | 'moderate' | 'good';
export const reports_progression_filter: ReportsProgressionFilterType[] = [ 'below', 'moderate', 'good' ];

export type ReportsQualityFilterType = 'low' | 'recommended' | 'high';
export const reports_quality_filter: ReportsQualityFilterType[] = [ 'low', 'recommended', 'high' ];

export type ReportsDefaultGroupFilterType = 'a' | 'b' | 'c'; // TODO Review this after adding final default groups
export const reports_default_group_filter: ReportsDefaultGroupFilterType[] = [ 'a', 'b', 'c' ];

export type ReportsCustomGroupFilterType = 'monday' | 'friday'; // TODO Review this after adding final custom groups
export const reports_custom_group_filter: ReportsCustomGroupFilterType[] = [ 'monday', 'friday' ];

export type ProgressConsistencyGroupType = 'occasional' | 'intermittent' | 'steady' | 'reliable' | 'constant';
export const progress_consistency_groups: ProgressConsistencyGroupType[] = [ 'occasional', 'intermittent', 'steady', 'reliable', 'constant' ];

export type StudentStatusGroupType = 'rookie' | 'apprentice' | 'pro' | 'master' | 'superstar';
export const student_status_groups: StudentStatusGroupType[] = [ 'rookie', 'apprentice', 'pro', 'master', 'superstar' ];

export type ReportsGenderFilterType = 'male' | 'female' | 'other';
export const reports_gender_filter: ReportsGenderFilterType[] = [ 'male', 'female', 'other' ];

export type ReportsTableViewType = 'report_table' | 'topic_progress_table';
export const reports_table_views: ReportsTableViewType[] = [ 'report_table', 'topic_progress_table' ];

export const tick_status_groups: TickStatusEnum[] = [ TickStatusEnum.RED, TickStatusEnum.AMBER, TickStatusEnum.GREEN ];

export type Locales = 'gb' | 'us' | 'ke' | 'nz' | 'cd' | 'mx' | 'th';
